import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ALL_FILTER_VALUE,
  DEFAULT_REPORT_TYPE,
  DEFAULT_TIME_PERIOD_PRESET,
  DOCUMENT_REPORT_ERROR_CODES,
  DOCUMENT_REPORT_FILTER_KEYS,
  EDOC_CONCRETE_VALUES,
  MAX_TIME_PERIOD_DAYS,
  SELECT_ALL_SEMANTICS_FILTER_KEYS,
  STATUS_CONCRETE_VALUES,
  applySelectAllSemantics,
  getDefaultDocumentReportFilters,
  stripAllFilterValue,
} from '../constants/documentReportFilterConstants';
import { getEnabledOptionValues, pruneDisabledSelections } from '../utils/documentReportScopeUtils';
import {
  computeTimePeriodDates,
  formatTimePeriodDisplayLabel,
  getTimePeriodDayCount,
  isTimePeriodValid,
} from '../utils/timePeriodUtils';

const createInitialFilters = () => ({
  ...getDefaultDocumentReportFilters(),
  timePeriod: {
    preset: DEFAULT_TIME_PERIOD_PRESET,
    ...computeTimePeriodDates(DEFAULT_TIME_PERIOD_PRESET),
  },
});

const getConcreteValues = (availableOptions = []) => getEnabledOptionValues(availableOptions);

const hasConcreteSelection = (values = []) => stripAllFilterValue(values).length > 0;

const buildAppliedFilters = (filters, options) => {
  const { categoryLabels = {}, timePeriodLabels = {} } = options;
  const applied = [];

  const appendMultiSelectFilters = (key, selectedValues, availableOptions = []) => {
    selectedValues.forEach((value) => {
      if (value === ALL_FILTER_VALUE) {
        return;
      }
      const option = availableOptions.find((item) => item.value === value);
      if (option && !option.disabled) {
        applied.push({ key, value, label: option.label, categoryLabel: categoryLabels[key] });
      }
    });
  };

  appendMultiSelectFilters(DOCUMENT_REPORT_FILTER_KEYS.TIN, filters.tin, options.tinOptions);
  appendMultiSelectFilters(DOCUMENT_REPORT_FILTER_KEYS.DOC_TYPE, filters.docTypes, options.docTypeOptions);
  appendMultiSelectFilters(DOCUMENT_REPORT_FILTER_KEYS.EDOC, filters.eDoc, options.eDocOptions);
  appendMultiSelectFilters(DOCUMENT_REPORT_FILTER_KEYS.STATUS, filters.status, options.statusOptions);

  if (filters.reportType && filters.reportType !== DEFAULT_REPORT_TYPE) {
    const reportTypeOption = (options.reportTypeOptions ?? []).find((item) => item.value === filters.reportType);
    if (reportTypeOption) {
      applied.push({
        key: DOCUMENT_REPORT_FILTER_KEYS.REPORT_TYPE,
        value: filters.reportType,
        label: reportTypeOption.label,
        categoryLabel: categoryLabels[DOCUMENT_REPORT_FILTER_KEYS.REPORT_TYPE],
      });
    }
  }

  if (filters.timePeriod?.preset !== DEFAULT_TIME_PERIOD_PRESET) {
    const periodLabel = formatTimePeriodDisplayLabel({ ...filters.timePeriod, labels: timePeriodLabels });
    if (periodLabel) {
      applied.push({
        key: DOCUMENT_REPORT_FILTER_KEYS.TIME_PERIOD,
        value: 'period',
        label: periodLabel,
        categoryLabel: categoryLabels[DOCUMENT_REPORT_FILTER_KEYS.TIME_PERIOD],
      });
    }
  }

  return applied;
};

const syncScopedSelections = (filters, filterOptions) => {
  const docTypes = pruneDisabledSelections(filters.docTypes, filterOptions.docTypeOptions ?? []);
  if (docTypes !== filters.docTypes) {
    return { ...filters, docTypes };
  }

  return filters;
};

const useDocumentReportFilters = ({ filterOptions }) => {
  const [filters, setFilters] = useState(() => createInitialFilters());
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setFilters((prev) => syncScopedSelections(prev, filterOptions));
  }, [filterOptions.docTypeOptions]);

  const concreteValuesByKey = useMemo(() => {
    const fromOptions = (options, fallback) => {
      const values = getConcreteValues(options);
      return values.length > 0 ? values : fallback;
    };

    return {
      [DOCUMENT_REPORT_FILTER_KEYS.DOC_TYPE]: fromOptions(filterOptions.docTypeOptions, []),
      [DOCUMENT_REPORT_FILTER_KEYS.EDOC]: fromOptions(filterOptions.eDocOptions, EDOC_CONCRETE_VALUES),
      [DOCUMENT_REPORT_FILTER_KEYS.STATUS]: fromOptions(filterOptions.statusOptions, STATUS_CONCRETE_VALUES),
    };
  }, [filterOptions.docTypeOptions, filterOptions.eDocOptions, filterOptions.statusOptions]);

  const updateFilter = useCallback(
    (key, value) => {
      setFilters((prev) => {
        if (SELECT_ALL_SEMANTICS_FILTER_KEYS.includes(key)) {
          return {
            ...prev,
            [key]: applySelectAllSemantics(prev[key], value, concreteValuesByKey[key] ?? []),
          };
        }

        return { ...prev, [key]: value };
      });
    },
    [concreteValuesByKey],
  );

  const removeAppliedFilter = useCallback(
    (key, value) => {
      setFilters((prev) => {
        if (key === DOCUMENT_REPORT_FILTER_KEYS.REPORT_TYPE) {
          return { ...prev, reportType: DEFAULT_REPORT_TYPE };
        }

        if (key === DOCUMENT_REPORT_FILTER_KEYS.TIME_PERIOD) {
          return {
            ...prev,
            timePeriod: {
              preset: DEFAULT_TIME_PERIOD_PRESET,
              ...computeTimePeriodDates(DEFAULT_TIME_PERIOD_PRESET),
            },
          };
        }

        const current = prev[key];
        if (!Array.isArray(current)) {
          return prev;
        }

        const next = current.filter((item) => item !== value);
        if (SELECT_ALL_SEMANTICS_FILTER_KEYS.includes(key)) {
          return {
            ...prev,
            [key]: applySelectAllSemantics(current, next, concreteValuesByKey[key] ?? []),
          };
        }

        return { ...prev, [key]: next };
      });
    },
    [concreteValuesByKey],
  );

  const clearAllFilters = useCallback(() => {
    setFilters(createInitialFilters());
    setTouched(false);
  }, []);

  const markTouched = useCallback(() => setTouched(true), []);

  const appliedFilters = useMemo(() => buildAppliedFilters(filters, filterOptions), [filterOptions, filters]);

  const dayCount = useMemo(
    () => getTimePeriodDayCount(filters.timePeriod?.startDate, filters.timePeriod?.endDate),
    [filters.timePeriod],
  );

  const validationErrors = useMemo(() => {
    const { startDate, endDate } = filters.timePeriod ?? {};

    let timePeriodError = '';
    if (!startDate || !endDate) {
      timePeriodError = DOCUMENT_REPORT_ERROR_CODES.REQUIRED;
    } else if (dayCount > MAX_TIME_PERIOD_DAYS) {
      timePeriodError = DOCUMENT_REPORT_ERROR_CODES.RANGE_TOO_LONG;
    }

    const tinError = filters.tin.length === 0 ? DOCUMENT_REPORT_ERROR_CODES.REQUIRED : '';
    const docTypesError = !hasConcreteSelection(filters.docTypes) ? DOCUMENT_REPORT_ERROR_CODES.REQUIRED : '';
    const eDocError = !hasConcreteSelection(filters.eDoc) ? DOCUMENT_REPORT_ERROR_CODES.REQUIRED : '';
    const statusError = !hasConcreteSelection(filters.status) ? DOCUMENT_REPORT_ERROR_CODES.REQUIRED : '';

    return {
      tin: tinError,
      docTypes: docTypesError,
      eDoc: eDocError,
      status: statusError,
      timePeriod: timePeriodError,
    };
  }, [dayCount, filters.docTypes, filters.eDoc, filters.status, filters.timePeriod, filters.tin]);

  const canGenerate = useMemo(
    () =>
      filters.tin.length > 0 &&
      hasConcreteSelection(filters.docTypes) &&
      hasConcreteSelection(filters.eDoc) &&
      hasConcreteSelection(filters.status) &&
      Boolean(filters.reportType) &&
      isTimePeriodValid(filters.timePeriod?.startDate, filters.timePeriod?.endDate) &&
      dayCount <= MAX_TIME_PERIOD_DAYS,
    [dayCount, filters.docTypes, filters.eDoc, filters.reportType, filters.status, filters.timePeriod, filters.tin],
  );

  const buildReportPayload = useCallback(
    (reportType) => ({
      created_after: filters.timePeriod.startDate?.format('YYYY-MM-DD') ?? null,
      created_before: filters.timePeriod.endDate?.format('YYYY-MM-DD') ?? null,
      type: reportType,
      filters: {
        tin: filters.tin,
        doc_types: stripAllFilterValue(filters.docTypes),
        documentMode: stripAllFilterValue(filters.eDoc),
        overallStatus: stripAllFilterValue(filters.status),
        report_type: filters.reportType,
      },
    }),
    [filters],
  );

  return {
    filters,
    appliedFilters,
    canGenerate,
    dayCount,
    touched,
    validationErrors,
    markTouched,
    updateFilter,
    removeAppliedFilter,
    clearAllFilters,
    buildReportPayload,
  };
};

export default useDocumentReportFilters;
